import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import {
  CircleHelp,
  Handshake,
  Eye,
  AlertTriangle,
  Search,
  Sparkles,
  Inbox,
  Clock,
  Flame,
  CheckCircle2,
  X,
} from 'lucide-react';
import axiosInstance from '../../services/api/axios';
import { toast } from 'react-toastify';
import DataTable from '../../components/DataTable';
import PaginationControls from '../../components/PaginationControls';
import DesignPhaseBadge from '../../components/interior/DesignPhaseBadge';
import CountdownBadge from '../../components/CountdownBadge';
import {
  hasStatus,
  ID_ACK_EXPIRED,
  ID_DELIVERY_EXPIRED,
} from '../../utils/idLeadStatus';

const STATUS_LABELS = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUOTED: 'Quoted',
  WON: 'Won',
  LOST: 'Lost',
  ID_ACKNOWLEDGEMENT_PENDING: 'Acknowledgement Pending',
  ID_DESIGN_REQUEST_ACKNOWLEDGED: 'Design Request Acknowledged',
  ID_DESIGN_SUBMITTED: 'Design Submitted',
  BUYER_DESIGN_APPROVED: 'Buyer Design Approved',
  BUYER_DESIGN_FEEDBACK_PROVIDED: 'Buyer Feedback Provided',
  BUYER_DESIGN_REJECTED: 'Buyer Design Rejected',
  ID_DESIGN_FEEDBACK_ACKNOWLEDGED: 'Designer Feedback Acknowledged',
  DEVELOPER_PENDING: 'Developer Pending',
  DEVELOPER_APPROVED: 'Developer Approved',
  DEVELOPER_REJECTED: 'Developer Rejected',
};

const STATUS_GUIDE_ORDER = [
  'NEW',
  'CONTACTED',
  'QUOTED',
  'WON',
  'LOST',
  'ID_ACKNOWLEDGEMENT_PENDING',
  'ID_DESIGN_REQUEST_ACKNOWLEDGED',
  'ID_DESIGN_SUBMITTED',
  'BUYER_DESIGN_APPROVED',
  'BUYER_DESIGN_FEEDBACK_PROVIDED',
  'BUYER_DESIGN_REJECTED',
  'ID_DESIGN_FEEDBACK_ACKNOWLEDGED',
  'DEVELOPER_PENDING',
  'DEVELOPER_APPROVED',
  'DEVELOPER_REJECTED',
];

const STATUS_EXPLANATIONS = {
  NEW:
    'The lead exists in the system. Next steps are usually to contact the buyer or review the property and configuration.',
  CONTACTED:
    'Someone from your side has reached out to the buyer or lead. Follow up until you move to a quote or design work.',
  QUOTED:
    'A commercial quote or scope has been shared with the buyer. The lead may move to won or lost from here.',
  WON:
    'The buyer accepted the engagement. Treat this as a successful conversion for reporting and next delivery steps.',
  LOST:
    'The buyer or project did not proceed. No further action is expected unless the lead is reopened elsewhere.',
  ID_ACKNOWLEDGEMENT_PENDING:
    'The platform is waiting for you to acknowledge this lead or the incoming design request before work continues.',
  ID_DESIGN_REQUEST_ACKNOWLEDGED:
    'You have acknowledged the buyer’s design request. You can proceed to prepare or submit your design.',
  ID_DESIGN_SUBMITTED:
    'You have uploaded or submitted a design iteration for the buyer to review.',
  BUYER_DESIGN_APPROVED:
    'The buyer approved your submitted design. You may continue with revisions only if they ask, or move toward closure depending on process.',
  BUYER_DESIGN_FEEDBACK_PROVIDED:
    'The buyer left feedback on your design. Review their comments and submit an updated iteration if required.',
  BUYER_DESIGN_REJECTED:
    'The buyer did not accept this design submission. Check feedback or contact them before submitting again.',
  ID_DESIGN_FEEDBACK_ACKNOWLEDGED:
    'You have acknowledged the buyer’s feedback so the workflow can advance (e.g. toward another submission or developer step).',
  DEVELOPER_PENDING:
    'The developer or builder must review or approve this lead or design before it can continue in the system.',
  DEVELOPER_APPROVED:
    'The developer approved the request or design. The lead can move forward in the pipeline.',
  DEVELOPER_REJECTED:
    'The developer did not approve this step. Check internal notes or requirements before resubmitting or closing the lead.',
};

const formatStatusLabel = (status) => {
  if (typeof status !== 'string' || status.trim().length === 0) return '—';
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const getLatestStatusTimestamp = (status) => {
  if (!Array.isArray(status) || status.length === 0) return Number.NEGATIVE_INFINITY;
  const latest = status
    .filter((entry) => entry && typeof entry === 'object' && Object.keys(entry).length > 0)
    .map((entry) => {
      const key = Object.keys(entry)[0];
      const value = entry?.[key];
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
    })
    .sort((a, b) => b - a)[0];
  return latest ?? Number.NEGATIVE_INFINITY;
};

const resolveStatusString = (raw) => {
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw) && raw.length > 0) {
    const latest = raw
      .filter((e) => e && typeof e === 'object' && Object.keys(e).length > 0)
      .map((e, idx) => {
        const key = Object.keys(e)[0];
        const parsed = Date.parse(e?.[key]);
        return { key, idx, time: Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed };
      })
      .sort((a, b) => (a.time === b.time ? b.idx - a.idx : b.time - a.time))[0];
    return latest?.key ?? '—';
  }
  if (raw && typeof raw === 'object') {
    return raw.typeName ?? raw.type ?? Object.keys(raw)[0] ?? '—';
  }
  return '—';
};

const getStatusTagClass = (status) => {
  switch (status) {
    case 'WON':
    case 'BUYER_DESIGN_APPROVED':
    case 'DEVELOPER_APPROVED':
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    case 'QUOTED':
    case 'ID_DESIGN_SUBMITTED':
      return 'bg-indigo-50 text-indigo-700 border border-indigo-200';
    case 'CONTACTED':
    case 'BUYER_DESIGN_FEEDBACK_PROVIDED':
    case 'ID_DESIGN_FEEDBACK_ACKNOWLEDGED':
      return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'LOST':
    case 'BUYER_DESIGN_REJECTED':
    case 'DEVELOPER_REJECTED':
      return 'bg-rose-50 text-rose-700 border border-rose-200';
    case 'ID_ACKNOWLEDGEMENT_PENDING':
    case 'ID_DESIGN_REQUEST_ACKNOWLEDGED':
    case 'DEVELOPER_PENDING':
      return 'bg-flamingo-50 text-flamingo-700 border border-flamingo-200';
    case 'NEW':
      return 'bg-slate-50 text-slate-700 border border-slate-200';
    default:
      return 'bg-slate-50 text-slate-600 border border-slate-200';
  }
};

/** Quick-filter buckets that group raw statuses into the 5 pill tabs above the table. */
const FILTER_BUCKETS = {
  all: () => true,
  active: (s, lead) =>
    !['WON', 'LOST', 'BUYER_DESIGN_REJECTED', 'DEVELOPER_REJECTED'].includes(s) &&
    !hasStatus(lead, ID_ACK_EXPIRED) &&
    !hasStatus(lead, ID_DELIVERY_EXPIRED),
  pending: (s) =>
    ['ID_ACKNOWLEDGEMENT_PENDING', 'ID_DESIGN_REQUEST_ACKNOWLEDGED', 'DEVELOPER_PENDING'].includes(s),
  overdue: (_s, lead) => hasStatus(lead, ID_ACK_EXPIRED) || hasStatus(lead, ID_DELIVERY_EXPIRED),
  won: (s) => s === 'WON' || s === 'BUYER_DESIGN_APPROVED' || s === 'DEVELOPER_APPROVED',
  lost: (s) => s === 'LOST' || s === 'BUYER_DESIGN_REJECTED' || s === 'DEVELOPER_REJECTED',
};

const Leads = () => {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStatusGuide, setShowStatusGuide] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  // Client-side pagination — the leads endpoint returns the full list, so we
  // slice it locally to keep the table scannable and match the rest of the app.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  useEffect(() => {
    if (!showStatusGuide) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowStatusGuide(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showStatusGuide]);

  const fetchLeads = useCallback(async () => {
    try {
      if (!userInfo?.loginId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const response = await axiosInstance.get(`/interior-designers/leads`);
      let leadsArray = [];
      if (Array.isArray(response)) leadsArray = response;
      else if (Array.isArray(response?.data)) leadsArray = response.data;
      else if (Array.isArray(response?.items)) leadsArray = response.items;
      else if (Array.isArray(response?.data?.items)) leadsArray = response.data.items;

      const sortedLeads = [...leadsArray].sort(
        (a, b) => getLatestStatusTimestamp(b?.status) - getLatestStatusTimestamp(a?.status)
      );
      setLeads(sortedLeads);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [userInfo?.loginId]);

  useEffect(() => {
    if (userInfo?.loginId) fetchLeads();
  }, [fetchLeads, userInfo?.loginId]);

  // KPI counts for the hero strip (computed across ALL leads, not just visible).
  const kpis = useMemo(() => {
    const totals = { total: leads.length, active: 0, pending: 0, overdue: 0, hot: 0 };
    leads.forEach((lead) => {
      const s = resolveStatusString(lead?.status);
      if (FILTER_BUCKETS.active(s, lead)) totals.active += 1;
      if (FILTER_BUCKETS.pending(s)) totals.pending += 1;
      if (FILTER_BUCKETS.overdue(s, lead)) totals.overdue += 1;
      if (lead?.hasNewRequest || (lead?.newRequests ?? 0) > 0) totals.hot += 1;
    });
    return totals;
  }, [leads]);

  // Pill tab definitions with live counts.
  const tabDefs = useMemo(
    () => [
      { id: 'all', label: 'All', count: kpis.total, icon: Inbox },
      { id: 'active', label: 'Active', count: kpis.active, icon: Sparkles },
      { id: 'pending', label: 'Pending', count: kpis.pending, icon: Clock },
      { id: 'overdue', label: 'Overdue', count: kpis.overdue, icon: AlertTriangle },
      { id: 'won', label: 'Won', count: leads.filter((l) => FILTER_BUCKETS.won(resolveStatusString(l?.status))).length, icon: CheckCircle2 },
      { id: 'lost', label: 'Lost', count: leads.filter((l) => FILTER_BUCKETS.lost(resolveStatusString(l?.status))).length, icon: X },
    ],
    [kpis, leads]
  );

  // Reset to first page when filters / search change.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchInput, pageSize]);

  const filteredLeads = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    const bucket = FILTER_BUCKETS[activeFilter] || FILTER_BUCKETS.all;
    return leads.filter((lead) => {
      const s = resolveStatusString(lead?.status);
      if (!bucket(s, lead)) return false;
      if (q) {
        const hay = [
          lead?.property?.propertyNo,
          lead?.propertyConfig?.configName,
          lead?.property?.projectPhase?.project?.projectName,
          lead?.interiorDesignerPortfolioItems?.[0]?.title,
          lead?.featuredDesignIterations?.[0]?.designIteration?.iterationName,
          lead?.featuredDesignIterations?.[0]?.designIteration?.iteration_name,
          formatStatusLabel(s),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [leads, searchInput, activeFilter]);

  // Slice for current page + build the pagination metadata object that
  // PaginationControls expects.
  const totalFiltered = filteredLeads.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const paginatedLeads = useMemo(
    () => filteredLeads.slice(pageStart, pageStart + pageSize),
    [filteredLeads, pageStart, pageSize]
  );
  // PaginationControls destructures camelCase (see component prop contract);
  // the previous snake_case keys silently left pageSize/totalPages/hasNext/
  // hasPrevious as undefined, which produced the "Showing NaN – NaN" footer
  // and always-disabled nav buttons.
  const paginationMeta = {
    page: safePage,
    pageSize: pageSize,
    total: totalFiltered,
    totalPages: totalPages,
    hasNext: safePage < totalPages,
    hasPrevious: safePage > 1,
  };

  const columns = [
    {
      key: 'property',
      header: 'Property',
      width: 110,
      render: (r) => (
        <span className="text-sm font-semibold text-slate-900 tabular-nums">
          {r?.property?.propertyNo || '—'}
        </span>
      ),
    },
    {
      key: 'config',
      header: 'Configuration',
      width: 150,
      render: (r) => (
        <span className="text-sm text-slate-700 truncate block" title={r?.propertyConfig?.configName}>
          {r?.propertyConfig?.configName || '—'}
        </span>
      ),
    },
    {
      key: 'project',
      header: 'Project',
      width: 180,
      render: (r) => (
        <span
          className="text-sm text-slate-700 truncate block"
          title={r?.property?.projectPhase?.project?.projectName}
        >
          {r?.property?.projectPhase?.project?.projectName || '—'}
        </span>
      ),
    },
    {
      key: 'design',
      header: 'Design',
      width: 200,
      render: (r) => {
        const featured = r?.featuredDesignIterations?.[0];
        const portfolio = r?.interiorDesignerPortfolioItems?.[0];
        const isSample = Boolean(featured);
        const name =
          (isSample &&
            (featured?.designIteration?.interactionName ||
              featured?.designIteration?.interaction_name ||
              featured?.designIteration?.iterationName ||
              featured?.designIteration?.iteration_name ||
              featured?.designIteration?.title ||
              featured?.designIteration?.designSubmission?.title ||
              (featured?.designIteration?.iterationNumber
                ? `Iteration ${featured.designIteration.iterationNumber}`
                : 'Sample Design'))) ||
          portfolio?.title ||
          '—';
        return (
          <div className="min-w-0">
            <p className="text-sm text-slate-800 truncate" title={name}>
              {name}
            </p>
            <p className="text-[11px] text-slate-500">
              {isSample ? 'Sample design' : 'Portfolio'}
            </p>
          </div>
        );
      },
    },
    {
      key: 'requests',
      header: 'Requests',
      width: 130,
      render: (r) => {
        const hasNew = r?.hasNewRequest;
        const count = r?.newRequests ?? 0;
        const isActive = hasNew || count > 0;
        if (!isActive) {
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600">
              None
            </span>
          );
        }
        return (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
              count > 0
                ? 'bg-flamingo-50 text-flamingo-700 border border-flamingo-200'
                : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
            }`}
          >
            <span className="requests-pulse-dot" />
            {count > 0 ? `+${count} new` : 'New request'}
          </span>
        );
      },
    },
    {
      key: 'deadlines',
      header: 'Deadlines',
      width: 220,
      render: (r) => {
        const ackExpired = hasStatus(r, ID_ACK_EXPIRED);
        const deliveryExpired = hasStatus(r, ID_DELIVERY_EXPIRED);
        // Once the lead has moved past the designer's submission window —
        // design submitted, buyer/dev approved or rejected — the designer's
        // deadlines no longer apply. Collapse the cell to "—".
        const designOutOfDesignerHands =
          hasStatus(r, 'ID_DESIGN_SUBMITTED') ||
          hasStatus(r, 'BUYER_DESIGN_APPROVED') ||
          hasStatus(r, 'BUYER_DESIGN_REJECTED') ||
          hasStatus(r, 'DEVELOPER_APPROVED') ||
          hasStatus(r, 'DEVELOPER_REJECTED');
        const showAckCountdown =
          Boolean(r?.acknowledgementDeadline) && !r?.isIdAcknowledged && !ackExpired && !designOutOfDesignerHands;
        const showDeliveryCountdown =
          Boolean(r?.designDeliveryDeadline) && !deliveryExpired && !designOutOfDesignerHands;
        // Hide the expired pills too once the design has moved on — they
        // describe a past state that's no longer actionable.
        const showAckExpired = ackExpired && !designOutOfDesignerHands;
        const showDeliveryExpired = deliveryExpired && !designOutOfDesignerHands;
        const nothingToShow =
          !showAckCountdown && !showDeliveryCountdown && !showAckExpired && !showDeliveryExpired;
        if (nothingToShow) return <span className="text-xs text-slate-400">—</span>;
        return (
          <div className="flex flex-wrap items-center gap-1">
            {showAckCountdown && (
              <CountdownBadge
                deadline={r.acknowledgementDeadline}
                label="Accept by"
                expiredLabel="Acceptance expired"
              />
            )}
            {!showAckCountdown && showAckExpired && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 border border-rose-300">
                <AlertTriangle size={12} className="shrink-0" aria-hidden />
                Acceptance expired
              </span>
            )}
            {showDeliveryCountdown && (
              <CountdownBadge
                deadline={r.designDeliveryDeadline}
                label="Deliver by"
                expiredLabel="Delivery overdue"
              />
            )}
            {!showDeliveryCountdown && showDeliveryExpired && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 border border-rose-300">
                <AlertTriangle size={12} className="shrink-0" aria-hidden />
                Delivery overdue
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: 170,
      render: (r) => {
        // Prefer the collapsed human phase; fall back to the raw status pill for older rows.
        if (r?.displayPhase) return <DesignPhaseBadge phase={r.displayPhase} />;
        const s = resolveStatusString(r?.status);
        const cls = getStatusTagClass(s);
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>
            {formatStatusLabel(s)}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'View',
      width: 70,
      align: 'center',
      render: (r) => (
        <button
          type="button"
          onClick={() => handleViewDetails(r)}
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 transition-colors"
          title="View lead"
          aria-label="View lead"
        >
          <Eye size={14} />
        </button>
      ),
    },
  ];

  const handleViewDetails = (lead) => {
    navigate(`/interior-designer/leads/${lead.interiorDesignerLeadId}`, { state: { lead } });
  };

  return (
    <div className="px-6 py-6 space-y-6">
      {/* Slate hero band with KPI strip */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)',
          }}
        />
        <div className="relative px-6 py-6 sm:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                <Handshake size={12} />
                Interior Designer · Leads
              </div>
              <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">Leads</h1>
              <p className="mt-1 text-sm text-white/70">
                Acknowledge new design requests, track buyer feedback, and keep delivery on schedule.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowStatusGuide(true)}
              className="self-start inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              <CircleHelp size={14} />
              Status guide
            </button>
          </div>

          {/* KPI strip */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Inbox size={13} />
                Total
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpis.total}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-emerald-200 text-xs">
                <Sparkles size={13} />
                Active
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpis.active}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-amber-200 text-xs">
                <Clock size={13} />
                Pending
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpis.pending}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-rose-200 text-xs">
                <AlertTriangle size={13} />
                Overdue
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpis.overdue}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10 col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 text-flamingo-200 text-xs">
                <Flame size={13} />
                New requests
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpis.hot}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter + search card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Pill tab nav */}
          <div className="overflow-x-auto">
            <div className="inline-flex items-center gap-1 rounded-2xl bg-slate-50 p-1.5">
              {tabDefs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveFilter(tab.id)}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-white hover:text-slate-800'
                    }`}
                  >
                    <Icon size={13} />
                    {tab.label}
                    <span
                      className={`tabular-nums rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                        isActive ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search */}
          <div className="relative flex-shrink-0 lg:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search property, project, design…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all focus:bg-white focus:border-flamingo-400 focus:ring-2 focus:ring-flamingo-200"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Result count */}
        {!loading && (
          <div className="mt-3 border-t border-slate-100 pt-2.5 text-xs text-slate-500">
            Showing <span className="font-semibold text-slate-700">{filteredLeads.length}</span> of{' '}
            <span className="font-semibold text-slate-700">{leads.length}</span>{' '}
            {leads.length === 1 ? 'lead' : 'leads'}
            {(activeFilter !== 'all' || searchInput.trim()) && (
              <button
                type="button"
                onClick={() => {
                  setActiveFilter('all');
                  setSearchInput('');
                }}
                className="ml-2 font-medium text-flamingo-600 hover:text-flamingo-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={paginatedLeads}
          loading={loading}
          minWidth={1320}
          rowKey={(r, i) => r?.interiorDesignerLeadId ?? i}
          emptyState={
            <div className="px-6 py-16 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                <Handshake className="text-slate-400" size={24} />
              </div>
              <p className="mt-4 text-sm font-semibold text-slate-700">
                {activeFilter === 'all' && !searchInput
                  ? 'No leads yet'
                  : 'No leads match the current filter'}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {activeFilter === 'all' && !searchInput
                  ? 'New design requests will appear here as buyers submit them.'
                  : 'Try clearing the filter or adjusting your search.'}
              </p>
            </div>
          }
        />
        {/* Inline pagination row — bare list variant, sits inside the same
            table card so it reads as one surface (not a separate block). */}
        {!loading && totalFiltered > 0 && (
          <div className="border-t border-slate-100 px-4 py-3">
            <PaginationControls
              pagination={paginationMeta}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              itemName="leads"
              variant="list"
              showInfo={true}
            />
          </div>
        )}
      </div>

      {/* Status guide modal */}
      {showStatusGuide && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="leads-status-guide-title"
          onClick={() => setShowStatusGuide(false)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Slate header — generous vertical padding so the chip + title +
                subtitle stack doesn't get clipped at the bottom of the band. */}
            <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-600 px-6 py-5 sm:py-6">
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.08]"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%)',
                }}
              />
              <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                    <CircleHelp size={11} />
                    Reference
                  </div>
                  <h2
                    id="leads-status-guide-title"
                    className="mt-2.5 text-lg sm:text-xl font-semibold leading-tight text-white"
                  >
                    Lead status guide
                  </h2>
                  <p className="mt-1.5 text-xs leading-relaxed text-white/70">
                    These labels match the Status column. When a history exists, the table shows
                    the latest step.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowStatusGuide(false)}
                  className="shrink-0 rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                  aria-label="Close status guide"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-6">
              <ul className="space-y-3">
                {STATUS_GUIDE_ORDER.map((code) => (
                  <li
                    key={code}
                    className="rounded-xl border border-slate-100 bg-slate-50/40 p-3 transition-colors hover:border-slate-200 hover:bg-white"
                  >
                    <div className="mb-1.5 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${getStatusTagClass(
                          code
                        )}`}
                      >
                        {STATUS_LABELS[code] ?? formatStatusLabel(code)}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-700">
                      {STATUS_EXPLANATIONS[code] ??
                        'Status is defined by your administrator or integration; open the lead for more context.'}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
